import classNames from 'classnames';

import Image from 'components/shared/image';
import VideoPlayer from 'components/shared/video/video';
import Button from 'components/shared/button';
import useIsMobile from 'hooks/utils/useIsMobile';
import renderInlineItalic from 'lib/utils/renderInlineItalic';
import styles from './hero.module.scss';

const Marquee = ({ headline, titleFont }) => {
  const isMobile = useIsMobile();
  if (!headline) return null;

  const AnimationSpeedModifier = isMobile ? 0.4 : 0.4;

  return (
    <div
      className={classNames(
        styles.marquee,
        styles.headline,
        titleFont !== 'Acumin Pro' && styles.octave
      )}
      style={{ '--marquee-speed': `${headline?.length * AnimationSpeedModifier}s` }}
    >
      <div className={styles.marqueeTrack}>
        <h2>{renderInlineItalic(headline)}</h2>
        <p aria-hidden="true">{renderInlineItalic(headline)}</p>
      </div>
    </div>
  );
};

const Hero = ({ data, locale }) => {
  const {
    headline,
    titleFont,
    body,
    media,
    button,
    subtitle,
    layout,
    contentAlignmentHorizontal,
    contentAlignmentHorizontalMobile,
    contentAlignmentVertical,
    contentAlignmentVerticalMobile,
    textColor,
    textColorMobile,
    anchorId,
  } = data;
  const titleClass = classNames(styles.headline, titleFont !== 'Acumin Pro' && styles.octave);

  const heroClassName = `hero${layout.replace(/\s+/g, '')}`;

  const customStyle = () => {
    const style = {};
    const positionMobile = contentAlignmentHorizontalMobile?.toLowerCase() || 'center';
    const position = contentAlignmentHorizontal?.toLowerCase() || positionMobile;
    const verticalPositionMobile = contentAlignmentVerticalMobile?.toLowerCase() || 'cenrter';
    const verticalPosition = contentAlignmentVertical?.toLowerCase() || verticalPositionMobile;

    const flexAlignValues = {
      left: 'flex-start',
      center: 'center',
      right: 'flex-end',
    };

    const justifyValues = {
      top: 'flex-start',
      center: 'center',
      bottom: 'flex-end',
    };

    style['--content-alignment-horizontal'] = position;
    style['--content-alignment-horizontal-mobile'] = positionMobile;
    style['--text-color'] = textColor;
    style['--text-color-mobile'] = textColorMobile;
    style['--flex-alignment'] = flexAlignValues[position] || 'center';
    style['--flex-alignment-mobile'] = flexAlignValues[positionMobile] || 'center';
    style['--justify-alignment'] = justifyValues[verticalPosition] || 'center';
    style['--justify-alignment-mobile'] = justifyValues[verticalPositionMobile] || 'center';

    return style;
  };

  return (
    <section
      id={anchorId}
      className={classNames(styles.hero, styles[heroClassName])}
      style={customStyle()}
    >
      <div className={styles.content}>
        {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        {layout === 'Marquee' && <Marquee headline={headline} titleFont={titleFont} />}

        {layout !== 'Marquee' && headline && (
          <h2 className={titleClass}>{renderInlineItalic(headline)}</h2>
        )}
        {body && <p className={styles.body}>{body}</p>}
        {button && (
          <div className={styles.buttonContainer}>
            <Button locale={locale} button={button} className={styles.button} />
          </div>
        )}
      </div>
      <div className={styles.mediaContainer}>
        {media?.type === 'Video' && media?.video ? (
          <VideoPlayer
            className={classNames(styles.media)}
            thumbnail={data?.media?.thumbnail}
            source={data.media.video}
            sourceDesktop={data.media.videoDesktop}
            autoPlay
            controls={false}
            forceKeepPlaying
          />
        ) : (
          <Image
            src={media.image}
            layout="fill"
            className={styles.media}
            loading="eager"
            priority
          />
        )}
      </div>
    </section>
  );
};

export default Hero;
